import { $api } from "@/utils/fetch.ts";
import type { GetNextPageParamFunction } from "@tanstack/react-query";

export interface Team {
	id: string;
	name: string;
	memberCount: number;
}

export interface TeamMember {
	userId: string;
	email: string;
}

export const useTeamsInfiniteQuery = ({
	search = "",
	pageSize = 10,
	/**
	 * Pass `false` to not issue the request at all. The endpoint is plan-gated
	 * (`teams/list.ts` → `requireFeature: "teams"`), so a caller that already knows
	 * the org's plan has no teams would otherwise fire a request whose only possible
	 * outcome is a 403.
	 */
	enabled = true,
}: {
	search?: string;
	pageSize?: number;
	enabled?: boolean;
}) => {
	const getNextPageParam: GetNextPageParamFunction<
		unknown,
		{ teams: Team[]; totalCount: number }
	> = (lastPage, allPages, lastPageParam) => {
		const loadedCount = allPages.reduce(
			(sum, page) => sum + (page.teams?.length || 0),
			0,
		);
		if (
			loadedCount < lastPage.totalCount &&
			typeof lastPageParam === "number"
		) {
			return lastPageParam + 1;
		}
		return undefined;
	};

	return $api.useInfiniteQuery(
		"get",
		"/api/teams",
		{
			params: {
				query: {
					pageSize,
					search,
				},
			},
		},
		{
			pageParamName: "page",
			getNextPageParam,
			initialPageParam: 0,
			enabled,
		},
	);
};

/**
 * All teams in the org, unpaginated (the list endpoint returns every row when no
 * `pageSize` is passed). Used where a complete id→name map is needed — e.g. the
 * Rights & Roles tool-access grid labelling its configured team rows, whose
 * config carries only team ids.
 */
export const allTeamsQueryOptions = () =>
	$api.queryOptions("get", "/api/teams", {});

export const teamQueryOptions = (teamId: string) =>
	$api.queryOptions("get", "/api/teams/{teamId}", {
		params: {
			path: {
				teamId,
			},
		},
	});

export const useCreateTeamMutation = () =>
	$api.useMutation("post", "/api/teams");

export const useUpdateTeamMutation = () =>
	$api.useMutation("put", "/api/teams/{teamId}");

export const useDeleteTeamMutation = () =>
	$api.useMutation("delete", "/api/teams/{teamId}");

// Team Members Queries and Mutations
export const teamMembersQueryOptions = (
	teamId: string,
	page?: number,
	pageSize?: number,
	search?: string,
) =>
	$api.queryOptions("get", "/api/teams/{teamId}/members", {
		params: {
			path: {
				teamId,
			},
			query: {
				page,
				pageSize,
				search,
			},
		},
	});

export const useAddTeamMemberMutation = () =>
	$api.useMutation("post", "/api/teams/{teamId}/members/add");

export const useRemoveTeamMemberMutation = () =>
	$api.useMutation("post", "/api/teams/{teamId}/members/remove");
